// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

// Employee margin (F5, Admin). The API mounts /v1/employee-margin on F5 only
// and gates it on Admin, like the route and the nav item.

const messageOf = (error, fallback) => {
  const status = error.response && error.response.status
  const serverMessage = error.response && error.response.data && error.response.data.message
  if (serverMessage) return serverMessage
  if (status === 403) return 'You do not have access to employee margin.'
  return error.message || fallback
}

// The latest precalculated snapshot: {run, attempt, people}.
export const getEmployeeMargin = createAsyncThunk('employeeMargin/get', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get('/v1/employee-margin')
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not load employee margin.'))
  }
})

// "Recalculate now": runs inline on the API and answers with the latest
// snapshot (the new run, or the previous one plus a Failed attempt). 409
// while another run is in flight.
export const recalculateEmployeeMargin = createAsyncThunk('employeeMargin/recalculate', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.post('/v1/employee-margin/recalculate')
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not recalculate employee margin.'))
  }
})

// Past periods (6m | 12m | ytd): the forecast replayed per closed month and
// summed over the period, with `recalculating` while its months are being
// replayed again (Recalculate). Cached per period: switching back to a
// loaded period does not refetch (`force` does, e.g. Try again).
export const getEmployeeMarginHistory = createAsyncThunk(
  'employeeMargin/history',
  async ({ period }, { rejectWithValue }) => {
    try {
      const response = await axios.get('/v1/employee-margin/history', { params: { period } })
      return response.data
    } catch (error) {
      return rejectWithValue(messageOf(error, 'Could not load the past period.'))
    }
  },
  {
    condition: ({ period, force } = {}, { getState }) => {
      const entry = getState().employeeMargin.history[period]
      if (!entry) return true
      if (entry.isLoading) return false
      return Boolean(force) || !entry.data
    }
  }
)

// A past period's "Recalculate": the API replays every month of the period in
// the background and answers 202 at once with the period and
// `recalculating`; 409 (with the same payload) while the period is already
// being recalculated. Both resolve with {data, alreadyRunning}: the page then
// polls the period until `recalculating` is null.
export const recalculateEmployeeMarginHistory = createAsyncThunk(
  'employeeMargin/historyRecalculate',
  async ({ period }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/v1/employee-margin/history/recalculate', null, { params: { period } })
      return { data: response.data, alreadyRunning: false }
    } catch (error) {
      const res = error && error.response
      if (res && res.status === 409 && res.data && res.data.period === period) {
        const { message, ...data } = res.data
        return { data, alreadyRunning: true }
      }
      return rejectWithValue(messageOf(error, 'Could not recalculate this period.'))
    }
  }
)

export const employeeMarginSlice = createSlice({
  name: 'employeeMargin',
  initialState: {
    data: null,
    isLoading: false,
    error: null,
    isRecalculating: false,
    recalcError: null,
    // period -> {data, isLoading, error}
    history: {},
    // period -> {isStarting, error, alreadyRunning}: a past period's Recalculate
    historyRecalc: {}
  },
  reducers: {
    clearRecalcError: (state) => {
      state.recalcError = null
    },
    // After a period's recalculation: the other periods' cached months may
    // be stale, so they load again when picked.
    dropOtherHistory: (state, action) => {
      const keep = action.payload
      Object.keys(state.history).forEach((period) => {
        if (period !== keep) delete state.history[period]
      })
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeMargin.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getEmployeeMargin.fulfilled, (state, action) => {
        state.isLoading = false
        state.data = action.payload
      })
      .addCase(getEmployeeMargin.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Could not load employee margin.'
      })
      .addCase(getEmployeeMarginHistory.pending, (state, action) => {
        const { period } = action.meta.arg
        state.history[period] = { ...(state.history[period] || { data: null }), isLoading: true, error: null }
      })
      .addCase(getEmployeeMarginHistory.fulfilled, (state, action) => {
        const { period } = action.meta.arg
        state.history[period] = { data: action.payload, isLoading: false, error: null }
        const recalc = state.historyRecalc[period]
        if (recalc && recalc.alreadyRunning && !(action.payload && action.payload.recalculating)) {
          state.historyRecalc[period] = { ...recalc, alreadyRunning: false }
        }
      })
      .addCase(getEmployeeMarginHistory.rejected, (state, action) => {
        const { period } = action.meta.arg
        state.history[period] = {
          ...(state.history[period] || { data: null }),
          isLoading: false,
          error: action.payload || 'Could not load the past period.'
        }
      })
      .addCase(recalculateEmployeeMarginHistory.pending, (state, action) => {
        const { period } = action.meta.arg
        state.historyRecalc[period] = { isStarting: true, error: null, alreadyRunning: false }
      })
      .addCase(recalculateEmployeeMarginHistory.fulfilled, (state, action) => {
        const { period } = action.meta.arg
        const { data, alreadyRunning } = action.payload
        state.historyRecalc[period] = { isStarting: false, error: null, alreadyRunning }
        // A bare {period, recalculating} (the API could not read the period
        // after starting) only updates the progress of what is loaded.
        const prev = state.history[period] && state.history[period].data
        const merged = Array.isArray(data && data.months) ? data : { ...(prev || {}), ...data }
        state.history[period] = { data: merged, isLoading: false, error: null }
      })
      .addCase(recalculateEmployeeMarginHistory.rejected, (state, action) => {
        const { period } = action.meta.arg
        state.historyRecalc[period] = {
          isStarting: false,
          error: action.payload || 'Could not recalculate this period.',
          alreadyRunning: false
        }
      })
      .addCase(recalculateEmployeeMargin.pending, (state) => {
        state.isRecalculating = true
        state.recalcError = null
      })
      .addCase(recalculateEmployeeMargin.fulfilled, (state, action) => {
        state.isRecalculating = false
        state.data = action.payload
        state.error = null
        // Past periods take their targets from the forecast run: reload them.
        state.history = {}
      })
      .addCase(recalculateEmployeeMargin.rejected, (state, action) => {
        state.isRecalculating = false
        state.recalcError = action.payload || 'Could not recalculate employee margin.'
      })
  }
})

export const { clearRecalcError, dropOtherHistory } = employeeMarginSlice.actions

export default employeeMarginSlice.reducer
